// MA-57: user-role primitive — stores whether the signed-in user is a
// parent or teacher (per-device, per-browser, stored in localStorage).
//
// Default = "parent" so every existing user keeps their current routing
// (personal-mode → /parent, and the kiosk-mode default landing on /play is
// unaffected since destination ignores role on kiosk — see
// resolveFrontDoorDestination below).
//
// Role is captured at the SIGNED-OUT front door (LoginScreen), not gated by
// deviceMode: deviceMode defaults to "kiosk" for every fresh install and is
// only changeable from inside /parent (Parent Settings), so a first-time
// teacher would have no path to ever flip it to "personal" before their
// first sign-in. The role chooser is shown unconditionally on LoginScreen
// instead, independent of deviceMode — "who are you" is answered before
// "is this device shared or personal".
//
// Role is per-device, like pw_device_mode — not tied to the Supabase user
// record.
//
// 2026-08-24 (MA-557, MA-DEC-0824) — the server-side follow-up this header
// promised (MA-58) has landed as src/lib/teacherAccount.ts: `pw_user_role`
// stays exactly what it always was, a per-device ROUTING/home-screen
// preference (a parent on a borrowed device choosing /play vs /parent is a
// real, legitimate choice this string still answers) — but it is no longer
// trusted for anything else. Every compliance fork and creation gate that
// used to key on `getUserRole()` directly now keys on the DERIVED,
// server-side account fact (owns a `classes` row OR holds a completed
// `school_authority_direct_v1` consent row) via `resolveEffectiveRole` /
// `deriveIsTeacherAccount` in teacherAccount.ts — closing a live bypass the
// MA-556 review found (docs/build/specs/MA-556-role-model-review.md, P3):
// flipping this device string could not, and still cannot, change what an
// account actually IS.
const KEY = "pw_user_role";
export type UserRole = "parent" | "teacher";

/** Pure parse — "teacher" → teacher; anything else (null, garbage, "parent") → parent. */
export function parseUserRole(raw: string | null | undefined): UserRole {
  return raw === "teacher" ? "teacher" : "parent";
}

/**
 * MA-289 slice (b) — a magic-link email lands in a FRESH browser context
 * (the user opens it from their mail app, possibly a different tab/device
 * than the one that requested it), so the tap-time localStorage write
 * (setUserRole in LoginScreen.handleRoleSelect) is NOT guaranteed to exist
 * on return the way it is for Google's same-tab OAuth redirect round-trip.
 * signInWithEmailOtp (supabaseClient.js) carries the chosen role in the
 * emailRedirectTo URL's `role` query param so routes/index.tsx can restamp
 * it on landing. Strict allowlist (unlike parseUserRole's permissive
 * default-to-parent) — an absent/garbage param means "no override", not
 * "override to parent".
 */
export function roleOverrideFromSearch(raw: unknown): UserRole | null {
  return raw === "teacher" || raw === "parent" ? raw : null;
}

export function getUserRole(): UserRole {
  if (typeof localStorage === "undefined") return "parent";
  return parseUserRole(localStorage.getItem(KEY));
}

export function setUserRole(role: UserRole): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, role);
  } catch {
    /* private mode / quota — keep in-memory snapshot in sync anyway */
  }
  refresh();
}

let snapshot: UserRole = getUserRole();
const listeners = new Set<() => void>();

function refresh(): void {
  snapshot = getUserRole();
  for (const l of listeners) l();
}

/**
 * Front-door destination fork — the single source of truth consumed by
 * BOTH routes/index.tsx's beforeLoad AND this file's own unit test, so the
 * test exercises the exact function the router calls (not a mirrored copy).
 *
 * MA-550 (founder live-QA 2026-08-22): `mode` is now the STRICT
 * getDeviceModeRaw() reading ("personal" | "kiosk" | null), not the
 * permissive getDeviceMode() default — ROOT CAUSE of the founder's bug was
 * that pw_device_mode defaults to "kiosk" on every fresh install, and the
 * old two-value signature had no way to tell "explicitly armed/declared
 * kiosk" apart from "never set at all." A first-time teacher who picked
 * "Signing in as a teacher" on LoginScreen had their role choice stored
 * correctly, then silently discarded here because the fork only ever saw
 * "kiosk" (the default) and role is IGNORED on kiosk by design — landing
 * them on the kid surface (/play) instead of /teacher. The 3×2 matrix:
 *
 *   explicit "kiosk"            → /play     (an armed/declared class device
 *                                             stays kid-first no matter who
 *                                             signs in — the classroom
 *                                             promise; role is IGNORED)
 *   explicit "personal" + parent  → /parent
 *   explicit "personal" + teacher → /teacher
 *   null (never set)   + parent   → /play    ← UNCHANGED, byte-for-byte:
 *                                             every existing family install
 *                                             (pw_device_mode never touched,
 *                                             pw_user_role never touched or
 *                                             explicitly "parent") keeps its
 *                                             current kid-first boot.
 *   null (never set)   + teacher  → /teacher ← THE FIX: the one cell that
 *                                             was broken. A brand-new
 *                                             install has no device-mode
 *                                             opinion yet, so a teacher's
 *                                             explicit role choice at the
 *                                             door now wins instead of
 *                                             being overridden by a default
 *                                             that was never actually
 *                                             chosen by anyone.
 */
//
// MA-928 (TUX-8): `armed` (this device is armed for a class, getArmedClass()
// !== null) sends an armed kiosk-mode device to the class door /play/who
// instead of the last child's home. It defaults to false, so every caller
// that does not pass it, and every unarmed device, returns exactly what the
// 3×2 matrix above returns.
export function resolveFrontDoorDestination(
  modeRaw: "personal" | "kiosk" | null,
  role: UserRole,
  armed: boolean = false,
): "/play" | "/play/who" | "/parent" | "/teacher" {
  if (modeRaw === "kiosk") return armed ? "/play/who" : "/play";
  if (modeRaw === "personal") return role === "teacher" ? "/teacher" : "/parent";
  // modeRaw === null — device has never been told what it is. Role alone
  // decides: a teacher gets their surface, everyone/everything else (parent,
  // or no role chosen at all — parseUserRole's own default) keeps the
  // pre-MA-550 kid-first landing.
  return role === "teacher" ? "/teacher" : "/play";
}
